import React, { useState, useMemo, useEffect, ReactElement } from 'react';
import WeekSelect from 'shared/components/WeekSelect';
import moment from 'moment';
import { Modal, Button, notification, Select, Popover, DatePicker } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import PeriodTag from 'shared/components/PeriodTag';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import NavigationPrompt from 'shared/components/NavigationPrompt';
import logoIcon from 'shared/images/logo_white.svg';
import logoText from 'shared/images/text_white.svg';

import './MenuClient.scss';
import {
    WeekData,
    ClientData,
    Data,
    MealMenu,
    Meal,
    EditedMeal,
    DishType,
    ClientIdentity,
    Dish,
} from 'shared/views/seniorCommand/types';
import {
    getDefaultDishes,
    getMealsAlerts,
    getWeekDaysArray,
    MealAlert,
} from 'shared/views/seniorCommand/functions';
import { INCREMENTABLE_DISH_TYPES } from 'shared/views/seniorCommand/constants';
import LoaderOverlay from 'shared/components/LoaderOverlay';
import ArrayService from 'shared/services/ArrayService';
import AlertTag from 'shared/components/AlertTag';
import { useLocation, useHistory } from 'react-router-dom';
import { showMailSendingError } from 'shared/services/FormService';

const GET_WEEK_DATA = gql`
    query getWeekData($startDate: Date, $endDate: Date) {
        mealMenus(clientTypes: ["senior"], startDate: $startDate, endDate: $endDate) {
            id
            date
            dishes {
                id
                dish {
                    id
                    name
                    type
                    allergies { id content }
                    noSideDish
                }
                choiceNumber
                sortOrder
            }
            dishTypes
            mealDefinition { id mealPeriods name singleDish }
        }
    }
`;

const GET_CLIENT_DATA = gql`
    query getClientData($startDate: Date, $endDate: Date, $clientId: Int) {
        client(id: $clientId) {
            id
            firstName
            lastName
            defaultDeliveryZone { id }
            menuWeekClients {
                id
                week { id weekNumber year }
                customerEditAt
            }
            clientIdentities {
                id
                firstname
                lastname
                isActive
                allergies { id content }
                diets { id content }
                defaultMeals {
                    id
                    weekDay
                    mealPeriod
                    meal { id }
                    deliveryDay
                    clientIdentityDefaultMealContents {
                        id
                        defaultMeal { id }
                        dish { id }
                        dishType
                        quantity
                    }
                }
                defaultDishes {
                    weekDay
                    mealPeriod
                    dishes { dish { id } quantity }
                }
                allowMultipleDishes
            }
        }

        clientIdentityAbsences(from: $startDate, to: $endDate, clientId: $clientId) {
            id
            date
            clientIdentity { id }
        }

        meals(from: $startDate, to: $endDate, clientId: $clientId) {
            id
            dishes { id dish { id } quantity dishType }
            price
            menu { id }
            command { id status client { id } }
            mealPeriod
            clientIdentity { id }
            date
            day
            orderNumber
        }
    }
`;

const GET_DATA = gql`
    query {
        dishTypes { code label }
        specialDiets { id content }
    }
`;

const USER_CLIENT_SUPPLEMENT = gql`
    query getData($userId: Int!) {
        user(id: $userId) {
            id
            clientSupplements {
                dish {
                    id
                    name
                    type
                    allergies { id content }
                    noSideDish
                }
            }
        }
    }
`;

const CREATE_COMMAND = gql`
    mutation createCommand($input: CommandInput!) {
        createCommand(input: $input) { id status }
    }
`;

const ORDER_COMMAND = gql`
    mutation orderCommand($id: UUID!) {
        order(id: $id) { id status }
    }
`;

const SAVE_MEALS = gql`
    mutation saveMeals($command: UUID!, $meals: [MealInput!]!) {
        saveMeals(meals: $meals, command: $command) {
            id
            dishes { id dish { id } quantity }
            price
            menu { id }
            command { id status client { id } }
            mealPeriod
            clientIdentity { id }
            date
            orderNumber
        }
    }
`;

const SEND_EMAIL_MENU_VALIDATION = gql`
    mutation sendEmailMenuValidation($id: Int!, $week: Int!, $year: Int!) {
        sendEmailMenuValidation(id: $id, week: $week, year: $year) { id }
    }
`;

const UPDATE_MENU_WEEK_CLIENT = gql`
    mutation updateMenuWeekClient($id: Int!, $input: MenuWeekClientInput!) {
        updateMenuWeekClient(id: $id, input: $input) { id }
    }
`;

const WEEK_DAY_MAP: { [day: string]: number } = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 0,
};

export default function MenuClient() {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const customerParam = searchParams.get('customer');
    const tokenParam = searchParams.get('token');
    const weekSelected = searchParams.get('week');
    const yearSelected = searchParams.get('year');

    const clientId = customerParam ? parseInt(customerParam, 10) : null;

    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
    const [selectedClientIdentityId, setSelectedClientIdentityId] = useState<string | null>(null);
    const [selectedOrderNumber, setMealOrderNumber] = useState<number | null>(null);
    const [authError, setAuthError] = useState<string | null>(null);

    const history = useHistory();

    // 🧭 Calcule le lundi de la semaine sélectionnée, ou le lundi de la semaine prochaine par défaut
    const [firstWeekDay, setFirstWeekDay] = useState(
        weekSelected
            ? moment()
                .year(yearSelected as any)
                .week(parseInt(weekSelected, 10))
                .startOf('week')
                .add(1, 'day')
                .format('YYYY-MM-DD')
            : moment().clone().weekday(1).format('YYYY-MM-DD')
    );

    const [lastWeekDay, setLastWeekDay] = useState(
        weekSelected
            ? moment()
                .year(yearSelected as any)
                .week(parseInt(weekSelected, 10))
                .startOf('week')
                .add(7, 'day')
                .format('YYYY-MM-DD')
            : moment().clone().weekday(7).format('YYYY-MM-DD')
    );

    const [saveDraftLoading, setSaveDraftLoading] = useState(false);
    const [saveCommandLoading, setSaveCommandLoading] = useState(false);

    const [editedMeals, setEditedMeals] = useState<EditedMeal[]>([]);

    const filteredEditedMeals = editedMeals.filter((meal) => meal.default != true);
    const navigationDisabled = filteredEditedMeals.length > 0;

    // ✅ NOUVEAU (vue jour par jour)
    const [dayIndex, setDayIndex] = useState(0);

    const { data: dataClientSupplement, refetch: refetchClientSupplement } = useQuery(
        USER_CLIENT_SUPPLEMENT,
        {
            variables: { userId: clientId },
            context: { headers: { Authorization: tokenParam ? `Bearer ${tokenParam}` : '' } },
            skip: !clientId,
            onError: (err: any) => {
                console.error('GraphQL error:', err);
                const message =
                    err?.networkError?.result?.error || err?.message || 'Erreur de connexion au serveur.';
                setAuthError(message);
            },
        }
    );

    const weekDays = useMemo(() => getWeekDaysArray(firstWeekDay), [firstWeekDay]);
    const weekDaysStr = weekDays.map((d) => moment(d).format('YYYY-MM-DD'));

    const startDate: any = weekDaysStr.length > 0 ? weekDaysStr[0] : null;
    const endDate: any = weekDaysStr.length > 0 ? weekDaysStr[weekDaysStr.length - 1] : null;

    const { data: data, error: error } = useQuery<Data>(GET_DATA, {
        context: { headers: { Authorization: tokenParam ? `Bearer ${tokenParam}` : '' } },
        fetchPolicy: 'cache-and-network',
    });

    const [localWeekData, setLocalWeekData] = useState<WeekData | null>(null);

    const { data: weekData, error: weekDataError } = useQuery<WeekData>(GET_WEEK_DATA, {
        variables: { startDate, endDate },
        context: { headers: { Authorization: tokenParam ? `Bearer ${tokenParam}` : '' } },
        fetchPolicy: 'cache-and-network',
        skip: !tokenParam,
        onError: (err: any) => {
            console.error('GraphQL error:', err);
            const message =
                err?.networkError?.result?.error || err?.message || 'Erreur de connexion au serveur.';
            setAuthError(message);
        },
    });

    const {
        data: clientData,
        loading: clientDataLoading,
        error: clientDataError,
        refetch: refetchClientData,
    } = useQuery<ClientData>(GET_CLIENT_DATA, {
        variables: { startDate, endDate, clientId: clientId },
        context: { headers: { 'X-Menu-Route': 'menu-client-valide', Authorization: tokenParam ? `Bearer ${tokenParam}` : '' } },
        fetchPolicy: 'network-only',
        skip: !clientId,
        onError: (err: any) => {
            console.error('GraphQL error:', err);
            const message =
                err?.networkError?.result?.error || err?.message || 'Erreur de connexion au serveur.';
            setAuthError(message);
        },
    });

    const [createCommand] = useMutation(CREATE_COMMAND);
    const [orderCommand] = useMutation(ORDER_COMMAND);
    const [saveMeals] = useMutation(SAVE_MEALS);
    const [updateMenuWeekClient] = useMutation(UPDATE_MENU_WEEK_CLIENT);
    const [sendEmailMenuValidation] = useMutation(SEND_EMAIL_MENU_VALIDATION, {
        onError: (err) => {
            showMailSendingError(err);
        },
    });

    const weekNumber = moment(firstWeekDay).week();
    const year = moment(firstWeekDay).year();
    const newYear = moment(lastWeekDay).year();

    const menuWeekClientInfos = clientData?.client?.menuWeekClients?.find(
        (weekClient: any) => weekClient.week.weekNumber === weekNumber && weekClient.week.year === year
    );

    function getFrenchHolidays(year: number): string[] {
        const easter = moment(calculateEaster(year));
        return [
            moment(`${year}-01-01`).format('YYYY-MM-DD'),
            moment(`${year}-05-01`).format('YYYY-MM-DD'),
            moment(`${year}-05-08`).format('YYYY-MM-DD'),
            easter.add(38, 'days').format('YYYY-MM-DD'),
            easter.add(49, 'days').format('YYYY-MM-DD'),
            easter.add(50, 'days').format('YYYY-MM-DD'),
            moment(`${year}-07-14`).format('YYYY-MM-DD'),
            moment(`${year}-08-15`).format('YYYY-MM-DD'),
            moment(`${year}-11-01`).format('YYYY-MM-DD'),
            moment(`${year}-11-11`).format('YYYY-MM-DD'),
            moment(`${year}-12-25`).format('YYYY-MM-DD'),
        ];
    }

    function calculateEaster(year: number): string {
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31);
        const day = ((h + l - 7 * m + 114) % 31) + 1;
        return `${year}-${month}-${day}`;
    }

    const commands = useMemo(
        () =>
            clientData ? clientData.meals.map((m: any) => m.command).filter(ArrayService.filterUniqueEntity) : [],
        [clientData]
    );

    // useEffect(() => {
    //     if (clientData) {
    //         if (menuWeekClientInfos?.customerEditAt != null) {
    //             history.push({ pathname: `/menu-client-used` });
    //         }
    //     }
    // }, [clientData, history]);

    useEffect(() => {
        if (data) {
            setSelectedClientId(clientData ? clientData.client.id + '' : null);

            const clientIdentityId =
                clientData && clientData.client.clientIdentities.find((i: ClientIdentity) => i.isActive);

            setSelectedClientIdentityId(clientIdentityId ? (clientIdentityId as any).id : null);

            setMealOrderNumber(selectedOrderNumber ? selectedOrderNumber : 1);
        }
        if (clientData) {
            if (menuWeekClientInfos?.customerEditAt != null) {
                history.push({ pathname: `/menu-client-used` });
            }
        }
    }, [data, history, selectedClientId, selectedClientIdentityId, selectedOrderNumber]);

    const currentClient: any = clientData ? clientData.client : null;
    const currentClientIdentity: ClientIdentity =
        currentClient && currentClient.clientIdentities.find((i: ClientIdentity) => i.isActive);

    const absenceDates: any = useMemo(
        () =>
            clientData
                ? clientData.clientIdentityAbsences
                    .filter((a: any) => currentClientIdentity && a.clientIdentity.id === currentClientIdentity.id)
                    .map((a: any) => moment(a.date))
                : [],
        [clientData, currentClientIdentity]
    );

    useEffect(() => {
        if (saveDraftLoading || saveCommandLoading || !clientData || !weekData || !currentClient) return;

        const clientMeals = clientData.meals.filter(
            (m: any) => currentClientIdentity.id === m.clientIdentity.id && m.orderNumber === selectedOrderNumber
        );

        const generateDefaultMeals = () => {
            return ([] as EditedMeal[]).concat(
                ...currentClient.clientIdentities.map(
                    (clientIdentity: any) =>
                        clientIdentity.defaultMeals
                            .map((defaultMeal: any) => {
                                const date = weekDays.find((d) => moment(d).day() === WEEK_DAY_MAP[defaultMeal.weekDay]);
                                const dateStr = moment(date).format('YYYY-MM-DD');

                                function getDefaultDeliveryDay(columnDate: any, deliveryZoneId: any, clientIdentityDefaultMeals?: any) {
                                    let columnMoment = moment(columnDate);

                                    if (clientIdentityDefaultMeals && Array.isArray(clientIdentityDefaultMeals)) {
                                        const dayOfWeek = columnMoment.day();
                                        const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                                        const weekdayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                                        const todayName = weekdayNames[dayIndex].toLowerCase();

                                        const matchingConfig = clientIdentityDefaultMeals.find((item: any) => {
                                            return (
                                                item &&
                                                item.weekDay &&
                                                item.weekDay.toLowerCase() === todayName &&
                                                item.deliveryDay !== null
                                            );
                                        });

                                        if (matchingConfig && matchingConfig.deliveryDay !== undefined) {
                                            const targetDay = (matchingConfig.deliveryDay + 1) % 7;

                                            const currentDay = columnMoment.day();
                                            let daysToAdjust;

                                            if (currentDay === targetDay) daysToAdjust = 0;
                                            else if (currentDay > targetDay) daysToAdjust = targetDay - currentDay;
                                            else daysToAdjust = targetDay - currentDay - 7;

                                            return columnMoment.add(daysToAdjust, 'days');
                                        }
                                    }

                                    const dayOfWeek = columnMoment.day();

                                    if (deliveryZoneId === 'b3b7b771-3cbb-471d-be99-8f7f417060c1') {
                                        if (dayOfWeek === 0) columnMoment = columnMoment.subtract(2, 'days');
                                        else if (dayOfWeek === 6) columnMoment = columnMoment.subtract(1, 'days');
                                        else if (dayOfWeek === 4) columnMoment = columnMoment.subtract(1, 'days');
                                        else if (dayOfWeek === 2) columnMoment = columnMoment.subtract(1, 'days');
                                    } else if (deliveryZoneId === '7d36dda0-2f3d-410d-ab2e-e4b984f703dd') {
                                        if (dayOfWeek === 0) columnMoment = columnMoment.subtract(1, 'days');
                                        else if (dayOfWeek === 5) columnMoment = columnMoment.subtract(1, 'days');
                                        else if (dayOfWeek === 3) columnMoment = columnMoment.subtract(1, 'days');
                                        else if (dayOfWeek === 1) columnMoment = columnMoment.subtract(2, 'days');
                                    } else {
                                        if (dayOfWeek === 0) columnMoment = columnMoment.subtract(2, 'days');
                                        else if (dayOfWeek === 6) columnMoment = columnMoment.subtract(1, 'days');
                                    }

                                    return columnMoment;
                                }

                                const deliveryDateDefault = getDefaultDeliveryDay(
                                    date,
                                    currentClient.defaultDeliveryZone.id,
                                    clientIdentity?.defaultMeals
                                ).format('YYYY-MM-DD');

                                if (
                                    clientData.clientIdentityAbsences.findIndex(
                                        (absenceDate: any) =>
                                            absenceDate.clientIdentity.id === clientIdentity.id && absenceDate.date === dateStr
                                    ) > -1
                                ) {
                                    return null;
                                }

                                const menu = weekData.mealMenus.find(
                                    (m: any) => m.date === dateStr && m.mealDefinition.id === defaultMeal.meal.id
                                );
                                if (!menu) return null;

                                return {
                                    mealPeriod: defaultMeal.mealPeriod,
                                    date: dateStr,
                                    menu: menu.id,
                                    dishes: defaultMeal.clientIdentityDefaultMealContents.map((d: any) => ({
                                        dish: d.dish.id,
                                        quantity: d.quantity,
                                        dishType: d.dishType,
                                    })),
                                    clientIdentity: clientIdentity.id,
                                    day: deliveryDateDefault,
                                    default: true,
                                };
                            })
                            .filter((m: EditedMeal | null) => !!m) as EditedMeal[]
                )
            );
        };

        if (!clientDataLoading && weekData && currentClient && clientMeals.length === 0) {
            setEditedMeals(generateDefaultMeals());
        } else {
            setEditedMeals((prev) => {
                const filteredPrev = prev.filter(
                    (editedMeal) =>
                        currentClient &&
                        currentClient.clientIdentities.findIndex(
                            (i: any) => i.id === editedMeal.clientIdentity && editedMeal.orderNumber === selectedOrderNumber
                        ) > -1 &&
                        weekDaysStr.includes(editedMeal.date)
                );

                const existingMeals = clientMeals
                    .filter(
                        (m: any) =>
                            m.dishes.length === 0 &&
                            filteredPrev.findIndex((p) => p.date === m.date && p.mealPeriod === m.mealPeriod) === -1
                    )
                    .map((meal: any) => {
                        const clientIdentity = currentClient.clientIdentities.find((i: any) => i.id === meal.clientIdentity.id);
                        const menu = weekData.mealMenus.find((m: any) => m.id === meal.menu.id);
                        const weekDay = Object.keys(WEEK_DAY_MAP).find((d) => WEEK_DAY_MAP[d] === moment(meal.date).day());

                        if (!clientIdentity || !menu || !weekDay) return null;

                        const dishes = getDefaultDishes(clientIdentity, weekDay, meal.mealPeriod, menu);

                        return {
                            ...meal,
                            clientIdentity: meal.clientIdentity.id,
                            menu: meal.menu.id,
                            command: meal.command.id,
                            dishes: dishes,
                            day: meal.day,
                            default: false,
                        };
                    })
                    .filter((m: any) => !!m) as EditedMeal[];

                const defaultMeals = generateDefaultMeals();
                const missingDefaultMeals = defaultMeals.filter((defaultMeal) => {
                    const existsInRegistered = clientMeals.some(
                        (registeredMeal: any) =>
                            registeredMeal.date === defaultMeal.date &&
                            registeredMeal.mealPeriod === defaultMeal.mealPeriod &&
                            registeredMeal.clientIdentity.id === defaultMeal.clientIdentity
                    );

                    const existsInPrev = filteredPrev.some(
                        (prevMeal) =>
                            prevMeal.date === defaultMeal.date &&
                            prevMeal.mealPeriod === defaultMeal.mealPeriod &&
                            prevMeal.clientIdentity === defaultMeal.clientIdentity
                    );

                    return !existsInRegistered && !existsInPrev;
                });

                return filteredPrev.concat(existingMeals).concat(missingDefaultMeals);
            });
        }
    }, [data, clientData, weekData, weekDays, currentClient, saveDraftLoading, saveCommandLoading, selectedOrderNumber]);

    useEffect(() => {
        if (!weekData || !dataClientSupplement) return;
        refetchClientSupplement();

        let updatedWeekData = JSON.parse(JSON.stringify(weekData));
        updatedWeekData.mealMenus.forEach((menu: any) => {
            dataClientSupplement.user.clientSupplements.forEach((supplement: any) => {
                if (!menu.dishes.some((dish: any) => dish.dish.id === supplement.dish.id)) {
                    menu.dishes.push({
                        id: supplement.dish.id,
                        dish: supplement.dish,
                        choiceNumber: null,
                        sortOrder: menu.dishes.length + 1,
                    });
                }
            });
        });

        setLocalWeekData(updatedWeekData);
    }, [weekData, dataClientSupplement]);

    // ✅ reset index quand on change de semaine
    useEffect(() => {
        setDayIndex(0);
    }, [firstWeekDay, startDate, endDate]);

    // if (authError) {
    //     return (
    //         <div className="menu-client-error">
    //             <AlertTag message={`Accès refusé : ${authError} Veuillez contacter La cigale.`} type="warning" />
    //         </div>
    //     );
    // }

    if (!data || !clientData || !weekData) return <LoaderOverlay loading />;
    if (error || clientDataError || weekDataError) return <div>Erreur...</div>;

    const currentClientMeals = currentClient
        ? clientData.meals.filter(
            (m: any) => currentClient.clientIdentities.findIndex((c: any) => c.id === m.clientIdentity.id) > -1
        )
        : [];

    const currentMeals = clientData.meals.filter(
        (m: any) => m.clientIdentity.id === (selectedClientIdentityId ? selectedClientIdentityId : (currentClientIdentity as any).id)
    );

    const currentCommand = currentMeals.length > 0 ? currentMeals[0].command : null;

    function saveCommand(andOrder?: boolean) {
        return new Promise<void>((resolve, reject) => {
            if (!currentClient) return reject();

            const cleanedEditedMeals = editedMeals
                .map((e) => {
                    const hasAlerts = alerts.some((a) => a.meal.date === e.date && a.meal.mealPeriod === e.mealPeriod);

                    const isSuccess =
                        !hasAlerts &&
                        (e.default !== true ||
                            (e.default == true &&
                                e.mealPeriod === 'evening' &&
                                e.dishes.length >= 2 &&
                                e.dishes.some((d: any) => d.dishType === 'dish')));

                    function getDefaultDeliveryDay(columnDate: any, deliveryZoneId: any) {
                        let columnMoment = moment(columnDate);
                        const dayOfWeek = columnMoment.day();

                        if (deliveryZoneId === 'b3b7b771-3cbb-471d-be99-8f7f417060c1') {
                            if (dayOfWeek === 0) columnMoment = columnMoment.subtract(2, 'days');
                            else if (dayOfWeek === 6) columnMoment = columnMoment.subtract(1, 'days');
                            else if (dayOfWeek === 4) columnMoment = columnMoment.subtract(1, 'days');
                            else if (dayOfWeek === 2) columnMoment = columnMoment.subtract(1, 'days');
                        } else if (deliveryZoneId === '7d36dda0-2f3d-410d-ab2e-e4b984f703dd') {
                            if (dayOfWeek === 0) columnMoment = columnMoment.subtract(1, 'days');
                            else if (dayOfWeek === 5) columnMoment = columnMoment.subtract(1, 'days');
                            else if (dayOfWeek === 3) columnMoment = columnMoment.subtract(1, 'days');
                            else if (dayOfWeek === 1) columnMoment = columnMoment.subtract(2, 'days');
                        } else {
                            if (dayOfWeek === 0) columnMoment = columnMoment.subtract(2, 'days');
                            else if (dayOfWeek === 6) columnMoment = columnMoment.subtract(1, 'days');
                        }

                        return columnMoment;
                    }

                    const deliveryDateDefault = getDefaultDeliveryDay(e.date, currentClient.defaultDeliveryZone.id).format('YYYY-MM-DD');

                    return {
                        ...e,
                        price: undefined,
                        __typename: undefined,
                        default: undefined,
                        dishes: e.dishes.map(({ dish, quantity }: any) => ({ dish, quantity })),
                        orderNumber: selectedOrderNumber,
                        day: e.day ? e.day : deliveryDateDefault,
                        success: isSuccess,
                    };
                })
                .filter((meal: any) => meal.success || (!meal.success && meal.id != null));

            if (!currentCommand) {
                createCommand({ variables: { input: { client: currentClient.id } } })
                    .then((res: any) => {
                        if (!res.data) return reject(reject);

                        saveMeals({ variables: { command: res.data.createCommand.id, meals: cleanedEditedMeals } })
                            .then(() => {
                                if (andOrder) {
                                    return orderCommand({ variables: { id: res.data.createCommand.id } })
                                        .then(() => resolve())
                                        .catch(reject);
                                }
                                resolve();
                            })
                            .then(() => {
                                if (menuWeekClientInfos) {
                                    updateMenuWeekClient({ variables: { id: menuWeekClientInfos.id, input: {} } });
                                }
                            })
                            .then(() => {
                                sendEmailMenuValidation({
                                    variables: { id: currentClient.id, week: weekNumber, year: year != newYear ? newYear : year },
                                });
                            })
                            .catch(reject);
                    })
                    .catch(reject);
            } else {
                saveMeals({ variables: { command: currentCommand.id, meals: cleanedEditedMeals } })
                    .then(() => {
                        sendEmailMenuValidation({
                            variables: { id: currentClient.id, week: weekNumber, year: year != newYear ? newYear : year },
                        });
                    })
                    .then(() => {
                        if (menuWeekClientInfos) {
                            updateMenuWeekClient({ variables: { id: menuWeekClientInfos.id, input: {} } });
                        }
                    })
                    .then(() => {
                        if (andOrder) {
                            return orderCommand({ variables: { id: currentCommand.id } })
                                .then(() => resolve())
                                .catch(reject);
                        }
                        resolve();
                    })
                    .catch(reject);
            }
        });
    }

    const clientAlerts = getMealsAlerts({
        client: currentClient,
        editedMeals: editedMeals,
        currentMeals: currentClientMeals,
        mealMenus: weekData.mealMenus,
    });

    const alerts = clientAlerts;

    const mealOptions = () => {
        const clientMealsOrder = clientData.meals.filter(
            (m: any) =>
                currentClient.clientIdentities.findIndex((i: any) => i.id === m.clientIdentity.id && m.orderNumber === selectedOrderNumber) > -1
        );

        const clientMeals = clientData.meals.filter(
            (m: any) =>
                currentClient.clientIdentities.findIndex((i: any) => i.id === m.clientIdentity.id) > -1
        );

        if (!clientMealsOrder || clientMealsOrder.length === 0) {
            if (selectedOrderNumber && selectedOrderNumber > 1) {
                const groupedOptions = clientMeals.reduce((acc: any, meal: any) => {
                    if (
                        meal &&
                        meal.orderNumber !== null &&
                        meal.orderNumber !== undefined &&
                        !acc.find((m: any) => m.value === meal.orderNumber)
                    ) {
                        acc.push({ label: `Menu ${meal.orderNumber}`, value: meal.orderNumber });
                    }
                    return acc;
                }, []);

                return [...groupedOptions];
            }
        } else {
            const groupedOptions = clientMeals.reduce((acc: any, meal: any) => {
                if (
                    meal &&
                    meal.orderNumber !== null &&
                    meal.orderNumber !== undefined &&
                    !acc.find((m: any) => m.value === meal.orderNumber)
                ) {
                    acc.push({ label: `Menu ${meal.orderNumber}`, value: meal.orderNumber });
                }
                return acc.sort((a: any, b: any) => a.value - b.value);
            }, []);

            return [...groupedOptions];
        }
    };

    function onFilterChange(field: any, value: any, force?: boolean) {
        if (!data) return;

        if (navigationDisabled && field !== 'clientIdentity' && !force) {
            Modal.confirm({
                title: 'Voulez-vous quitter la page ?',
                content: 'Les données saisies ne seront pas enregistrées.',
                onOk() {
                    setEditedMeals([]);
                    onFilterChange(field, value, true);
                },
            });
            return;
        }

        switch (field) {
            case 'orderNumber':
                setMealOrderNumber(value);
                break;
        }
    }

    // ============================
    // ✅ NOUVELLE LOGIQUE "NEXT"
    // ============================

    const isLastDay = dayIndex >= weekDays.length - 1;
    const currentDayDate = weekDays[dayIndex];
    const currentDayStr = currentDayDate ? moment(currentDayDate).format('YYYY-MM-DD') : null;

    const isAbsentToday =
        !!currentDayStr &&
        absenceDates.findIndex((d: any) => currentDayStr === d.format('YYYY-MM-DD')) > -1;

    function getMealForDayPeriod(dateStr: string, period: 'noon' | 'evening'): EditedMeal | null {
        // edited meal ?
        const edited = editedMeals.find(
            (m: any) =>
                currentClientIdentity &&
                m.clientIdentity === currentClientIdentity.id &&
                m.mealPeriod === period &&
                m.date === dateStr
        );
        if (edited) return edited;

        // existing meal ?
        const existing = currentMeals.find(
            (m: any) =>
                currentClientIdentity &&
                m.clientIdentity.id === currentClientIdentity.id &&
                m.mealPeriod === period &&
                m.date === dateStr &&
                m.orderNumber === selectedOrderNumber
        );

        if (!existing) return null;

        return {
            ...(existing as any),
            command: (existing as any).command.id,
            menu: (existing as any).menu?.id,
            clientIdentity: (existing as any).clientIdentity.id,
            orderNumber: selectedOrderNumber as any,
            dishes: (existing as any).dishes.map((d: any) => ({
                dish: d.dish.id,
                quantity: d.quantity,
                dishType: d.dishType,
            })),
        } as any;
    }

    function isMealSuccessLikeSave(meal: EditedMeal | null, dateStr: string, period: 'noon' | 'evening') {
        if (!meal) return false;

        const hasAlerts = alerts.some((a) => a.meal.date === dateStr && a.meal.mealPeriod === period);

        const okDefaultEvening =
            (meal as any).default == true &&
            period === 'evening' &&
            (meal as any).dishes?.length >= 2 &&
            (meal as any).dishes?.some((d: any) => d.dishType === 'dish');

        const ok =
            !hasAlerts &&
            ((meal as any).default !== true || okDefaultEvening);

        return ok;
    }

    const canGoNext =
        isAbsentToday ||
        !currentDayStr ||
        (() => {
            const noonMeal = getMealForDayPeriod(currentDayStr, 'noon');
            const eveningMeal = getMealForDayPeriod(currentDayStr, 'evening');

            const noonMenuNull = !noonMeal || (noonMeal as any).menu == null;
            const eveningMenuNull = !eveningMeal || (eveningMeal as any).menu == null;

            const noonOk = noonMenuNull || isMealSuccessLikeSave(noonMeal, currentDayStr, 'noon');
            const eveningOk = eveningMenuNull || isMealSuccessLikeSave(eveningMeal, currentDayStr, 'evening');

            return noonOk && eveningOk;
        })();

    // vue jour = on passe un tableau avec 1 jour aux composants table
    const singleDayArray: Date[] = currentDayDate ? [currentDayDate] : [];

    return (
        <div className="senior-commands-client">
            <div className="__banner">
                <div className="__logo">
                    <img className="__icon" src={logoIcon} alt="Logo La Cigale" />
                    <img className="__text" src={logoText} alt="La Cigale" />
                </div>
            </div>
            {/*<NavigationPrompt enabled={navigationDisabled}/>*/}

            <div className="__header">
                <h2>{`Saisie des repas de ${currentClient.firstName} ${currentClient.lastName}`}</h2>
                <WeekSelect
                    value={firstWeekDay}
                    startWeekDay={1}
                    modificationDays={true}
                    onChange={(v) => {
                        const newDateStr = moment(v).add(1, 'day').format('YYYY-MM-DD');
                        if (navigationDisabled) {
                            Modal.confirm({
                                title: 'Voulez-vous quitter la page ?',
                                content: 'Les données saisies ne seront pas enregistrées.',
                                onOk() {
                                    setFirstWeekDay(newDateStr);
                                },
                            });
                        } else {
                            setFirstWeekDay(newDateStr);
                        }

                        setMealOrderNumber(1);
                    }}
                />
            </div>

            <div className="__filters">
                <div className="__field">
                    <div className="__label">Sélection du menu</div>

                    <Select
                        onChange={(value: any) => {
                            if (value === 'newOrderNumber') {
                                const clientMeals = clientData.meals.filter(
                                    (m: any) =>
                                        currentClient.clientIdentities.findIndex((i: any) => i.id === m.clientIdentity.id) > -1
                                );

                                const maxOrderNumber = Math.max(
                                    ...(clientMeals.map((meal: any) => meal.orderNumber).filter((n) => n !== null && n !== undefined) as any),
                                    0
                                );

                                const newOrderNumber = maxOrderNumber + 1;
                                onFilterChange('orderNumber', newOrderNumber);
                            } else {
                                onFilterChange('orderNumber', value);
                            }
                        }}
                        value={selectedOrderNumber ? `Menu ${selectedOrderNumber}` : undefined}
                        options={mealOptions()}
                    />
                </div>

                <div className="__field __actions">
                    <div className="__save-command-btn">
                        {currentClient && clientAlerts.length > 0 && (
                            <Popover
                                content={
                                    <div>
                                        {currentClient.clientIdentities.map((clientIdentity: any) => {
                                            const clientIdentityAlerts = clientAlerts
                                                .filter((a) => a.clientIdentity === clientIdentity.id)
                                                .filter(ArrayService.filterUniqueBy('message'));

                                            if (clientIdentityAlerts.length === 0) return null;

                                            return (
                                                <div key={clientIdentity.id}>
                                                    <h5>{`${clientIdentity.firstname} ${clientIdentity.lastname}`}</h5>
                                                    {clientIdentityAlerts.map((alert: any) => (
                                                        <AlertTag key={alert.message} message={alert.message} />
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </div>
                                }
                            >
                                <div>
                                    <AlertTag message={`${clientAlerts.length} alertes`} />
                                </div>
                            </Popover>
                        )}
                    </div>
                </div>
            </div>

            {/* ✅ NAV JOUR PAR JOUR */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 12px 12px',
                    gap: 12,
                }}
            >
                <Button
                    shape="round"
                    onClick={() => setDayIndex((i) => Math.max(0, i - 1))}
                    disabled={dayIndex === 0}
                >
                    Jour précédent
                </Button>

                <div style={{flex: 1, textAlign: 'center', fontWeight: 600}}>

                </div>
                    <Button
                        shape="round"
                        onClick={() => setDayIndex((i) => Math.min(weekDays.length - 1, i + 1))}
                        disabled={dayIndex >= weekDays.length - 1}
                    >
                        Jour suivant
                    </Button>
            </div>


            <div className="__content">
                {clientDataLoading && <LoaderOverlay loading/>}
                <table className="__menu-table">
                    <colgroup>
                        {singleDayArray.map((date) => {
                            const dateStr = moment(date).format('YYYY-MM-DD');
                            const isAbsent = absenceDates.findIndex((d: any) => dateStr === d.format('YYYY-MM-DD')) > -1;

                            return (
                                <col
                                    key={dateStr}
                                    width="100%"
                                    style={isAbsent ? {background: '#F1F1F1'} : undefined}
                                />
                            );
                        })}
                    </colgroup>

                    <thead>
                    <tr>
                        {singleDayArray.map((date) => (
                            <th key={moment(date).format('YYMMDD')}>
                                <div>
                                    {moment(date).format('dddd').toUpperCase()} ({moment(date).format('DD')})
                                </div>
                            </th>
                        ))}
                    </tr>
                    </thead>

                    <tbody>
                    <TableAlertRow alerts={alerts} weekDays={singleDayArray} absenceDates={absenceDates}/>

                    <TablePeriodRow
                        period="noon"
                        client={currentClient}
                        clientIdentity={currentClientIdentity}
                        orderNumber={selectedOrderNumber as any}
                        dishTypes={data.dishTypes}
                        weekDays={singleDayArray}
                        mealMenus={weekData.mealMenus}
                        editedMeals={editedMeals}
                        meals={currentMeals}
                        onChange={setEditedMeals}
                        alerts={alerts}
                        localWeekData={localWeekData}
                    />

                    <TablePeriodRow
                        period="evening"
                        client={currentClient}
                        clientIdentity={currentClientIdentity}
                        orderNumber={selectedOrderNumber as any}
                        dishTypes={data.dishTypes}
                        weekDays={singleDayArray}
                        mealMenus={weekData.mealMenus}
                        editedMeals={editedMeals}
                        meals={currentMeals}
                        onChange={setEditedMeals}
                        alerts={alerts}
                        localWeekData={localWeekData}
                    />
                    </tbody>
                </table>
            </div>
        </div>
    );
}

interface TableAlertRowProps {
    alerts: MealAlert[];
    weekDays: Date[];
    absenceDates: moment.Moment[];
}

const TableAlertRow: React.FunctionComponent<TableAlertRowProps> = ({ weekDays, alerts, absenceDates }) => {
    if (alerts.length === 0 && absenceDates.length === 0) return null;

    return (
        <tr className="__table-alert-row">
            {weekDays.map((date) => {
                const dateStr = moment(date).format('YYYY-MM-DD');
                const dateAlerts = alerts.filter((a) => a.meal.date === dateStr);

                return (
                    <td key={dateStr}>
                        {absenceDates.findIndex((absenceDate) => absenceDate.format('YYYY-MM-DD') === dateStr) > -1 && (
                            <AlertTag message="Déclaré absent" type="info" />
                        )}
                        {dateAlerts.map((alert) => (
                            <AlertTag
                                key={alert.meal.id + alert.meal.mealPeriod + alert.message}
                                message={alert.message}
                            />
                        ))}
                    </td>
                );
            })}
        </tr>
    );
};

interface TablePeriodRowProps {
    client: any;
    clientIdentity?: ClientIdentity;
    orderNumber: number;
    weekDays: Date[];
    dishTypes: DishType[];
    period: string;
    mealMenus: MealMenu[];
    editedMeals: EditedMeal[];
    meals: Meal[];
    onChange: (editedMeals: EditedMeal[]) => void;
    alerts: MealAlert[];
    localWeekData: WeekData | null;
}

const TablePeriodRow: React.FunctionComponent<TablePeriodRowProps> = ({
                                                                          client,
                                                                          clientIdentity,
                                                                          orderNumber,
                                                                          weekDays,
                                                                          dishTypes,
                                                                          period,
                                                                          mealMenus,
                                                                          editedMeals,
                                                                          meals,
                                                                          onChange,
                                                                          alerts,
                                                                          localWeekData,
                                                                      }) => {
    function handleChange(newMeal: EditedMeal) {
        const iMeal = editedMeals.findIndex(
            (m: any) =>
                clientIdentity &&
                m.clientIdentity === clientIdentity.id &&
                m.mealPeriod === newMeal.mealPeriod &&
                m.date === newMeal.date
        );

        const newEditedMeals = [...editedMeals];
        if (iMeal > -1) newEditedMeals[iMeal] = newMeal;
        else newEditedMeals.push(newMeal);

        onChange(newEditedMeals);
    }

    const weekDaysStr = weekDays.map((date: any) => moment(date).format('YYYY-MM-DD'));

    let mealByDates: { [date: string]: EditedMeal | null } = {};
    let menuByDates: { [date: string]: MealMenu | undefined } = {};

    weekDaysStr.forEach((date: any) => {
        const editedMeal = editedMeals.find(
            (m: any) => clientIdentity && m.clientIdentity === clientIdentity.id && m.mealPeriod === period && m.date === date
        );

        const existingMeal = meals.find(
            (m: any) =>
                clientIdentity &&
                m.clientIdentity.id === clientIdentity.id &&
                m.mealPeriod === period &&
                m.date === date &&
                m.orderNumber === orderNumber
        );

        const meal: EditedMeal | null =
            editedMeal ||
            (existingMeal
                ? ({
                    ...existingMeal,
                    command: (existingMeal as any).command.id,
                    menu: (existingMeal as any).menu.id,
                    clientIdentity: (existingMeal as any).clientIdentity.id,
                    orderNumber: orderNumber,
                    dishes: (existingMeal as any).dishes.map((d: any) => ({
                        dish: d.dish.id,
                        quantity: d.quantity,
                        dishType: d.dishType,
                    })),
                } as any)
                : null);

        const menu = meal ? mealMenus.find((m: any) => m.id === (meal as any).menu) : undefined;

        mealByDates[date] = meal;
        menuByDates[date] = menu;
    });

    const usedDishTypes = dishTypes.filter((dishType: any) => {
        return Object.values(menuByDates).findIndex((menu) => menu?.dishTypes.includes(dishType.code)) > -1;
    });

    return (
        <>
            <tr>
                {weekDaysStr.map((date) => (
                    <td key={date}>
                        <PeriodTag period={period} line />
                    </td>
                ))}
            </tr>

            <tr>
                {weekDaysStr.map((date) => {
                    const dateMenus = mealMenus.filter(
                        (m: any) => m.date === date && m.mealDefinition.mealPeriods.includes(period)
                    );

                    const meal = mealByDates[date];
                    const menu = menuByDates[date];

                    const hasMealRecorded =
                        !!meal &&
                        !!menu &&
                        Array.isArray(meal.dishes) &&
                        meal.dishes.length > 0;

                    // (tout ton code DatePicker + disabledDate inchangé)
                    function calculateEaster(year: number): string {
                        const a = year % 19;
                        const b = Math.floor(year / 100);
                        const c = year % 100;
                        const d = Math.floor(b / 4);
                        const e = b % 4;
                        const f = Math.floor((b + 8) / 25);
                        const g = Math.floor((b - f + 1) / 3);
                        const h = (19 * a + b - d - g + 15) % 30;
                        const i = Math.floor(c / 4);
                        const k = c % 4;
                        const l = (32 + 2 * e + 2 * i - h - k) % 7;
                        const m = Math.floor((a + 11 * h + 22 * l) / 451);
                        const month = Math.floor((h + l - 7 * m + 114) / 31);
                        const day = ((h + l - 7 * m + 114) % 31) + 1;
                        return `${year}-${month}-${day}`;
                    }

                    function getFrenchHolidays(year: number): string[] {
                        const easter = moment(calculateEaster(year));
                        return [
                            moment(`${year}-01-01`).format('YYYY-MM-DD'),
                            moment(`${year}-05-01`).format('YYYY-MM-DD'),
                            moment(`${year}-05-08`).format('YYYY-MM-DD'),
                            easter.add(38, 'days').format('YYYY-MM-DD'),
                            easter.add(49, 'days').format('YYYY-MM-DD'),
                            easter.add(50, 'days').format('YYYY-MM-DD'),
                            moment(`${year}-07-14`).format('YYYY-MM-DD'),
                            moment(`${year}-08-15`).format('YYYY-MM-DD'),
                            moment(`${year}-11-01`).format('YYYY-MM-DD'),
                            moment(`${year}-11-11`).format('YYYY-MM-DD'),
                            moment(`${year}-12-25`).format('YYYY-MM-DD'),
                        ];
                    }

                    function getDefaultDeliveryDay(columnDate: any, deliveryZoneId: any, clientIdentityDefaultMeals?: any) {
                        let columnMoment = moment(columnDate);

                        if (clientIdentityDefaultMeals && Array.isArray(clientIdentityDefaultMeals)) {
                            const dayOfWeek = columnMoment.day();
                            const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                            const weekdayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                            const todayName = weekdayNames[dayIndex].toLowerCase();

                            const matchingConfig = clientIdentityDefaultMeals.find((item: any) => {
                                return item && item.weekDay && item.weekDay.toLowerCase() === todayName && item.deliveryDay !== null;
                            });

                            if (matchingConfig && matchingConfig.deliveryDay !== undefined) {
                                const targetDay = (matchingConfig.deliveryDay + 1) % 7;
                                const currentDay = columnMoment.day();
                                let daysToAdjust;

                                if (currentDay === targetDay) daysToAdjust = 0;
                                else if (currentDay > targetDay) daysToAdjust = targetDay - currentDay;
                                else daysToAdjust = targetDay - currentDay - 7;

                                return columnMoment.add(daysToAdjust, 'days');
                            }
                        }

                        const dayOfWeek = columnMoment.day();

                        if (deliveryZoneId === 'b3b7b771-3cbb-471d-be99-8f7f417060c1') {
                            if (dayOfWeek === 0) columnMoment = columnMoment.subtract(2, 'days');
                            else if (dayOfWeek === 6) columnMoment = columnMoment.subtract(1, 'days');
                            else if (dayOfWeek === 4) columnMoment = columnMoment.subtract(1, 'days');
                            else if (dayOfWeek === 2) columnMoment = columnMoment.subtract(1, 'days');
                        } else if (deliveryZoneId === '7d36dda0-2f3d-410d-ab2e-e4b984f703dd') {
                            if (dayOfWeek === 0) columnMoment = columnMoment.subtract(1, 'days');
                            else if (dayOfWeek === 5) columnMoment = columnMoment.subtract(1, 'days');
                            else if (dayOfWeek === 3) columnMoment = columnMoment.subtract(1, 'days');
                            else if (dayOfWeek === 1) columnMoment = columnMoment.subtract(2, 'days');
                        } else {
                            if (dayOfWeek === 0) columnMoment = columnMoment.subtract(2, 'days');
                            else if (dayOfWeek === 6) columnMoment = columnMoment.subtract(1, 'days');
                        }

                        return columnMoment;
                    }

                    function isDisabledDate(current: any, columnDate: any, deliveryZoneId: any) {
                        const columnMoment = moment(columnDate);
                        const dayOfWeek = columnMoment.day();

                        if (deliveryZoneId === 'b3b7b771-3cbb-471d-be99-8f7f417060c1') {
                            switch (dayOfWeek) {
                                case 5:
                                    return current && (current < columnMoment.subtract(2, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                case 6:
                                    return current && (current < columnMoment.subtract(3, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                case 0:
                                    return current && (current < columnMoment.subtract(4, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                case 1:
                                    return !current.isSame(columnMoment, 'day');
                                case 2:
                                    return current && (current < columnMoment.subtract(2, 'days').startOf('day') || current > columnMoment.add(0, 'days').endOf('day'));
                                case 3:
                                    return current && (current < columnMoment.subtract(2, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                case 4:
                                    return current && (current < columnMoment.subtract(3, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                default:
                                    return false;
                            }
                        } else if (deliveryZoneId === '7d36dda0-2f3d-410d-ab2e-e4b984f703dd') {
                            switch (dayOfWeek) {
                                case 5:
                                    return current && (current < columnMoment.subtract(3, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                case 6:
                                    return current && (current < columnMoment.subtract(2, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                case 0:
                                    return current && (current < columnMoment.subtract(3, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                case 1:
                                    return current && (current < columnMoment.subtract(4, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                case 2:
                                    return current && (current < columnMoment.subtract(1, 'days').startOf('day') || current > columnMoment.add(1, 'days').endOf('day'));
                                case 3:
                                    return current && (current < columnMoment.subtract(2, 'days').startOf('day') || current > columnMoment.add(1, 'days').endOf('day'));
                                case 4:
                                    return current && (current < columnMoment.subtract(2, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                default:
                                    return false;
                            }
                        } else {
                            switch (dayOfWeek) {
                                case 5:
                                    return current && (current < columnMoment.subtract(2, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                case 6:
                                    return current && (current < columnMoment.subtract(3, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                case 0:
                                    return current && (current < columnMoment.subtract(4, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                case 1:
                                    return !current.isSame(columnMoment, 'day');
                                case 2:
                                    return current && (current < columnMoment.subtract(1, 'days').startOf('day') || current > columnMoment.add(1, 'days').endOf('day'));
                                case 3:
                                    return current && (current < columnMoment.subtract(2, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                case 4:
                                    return current && (current < columnMoment.subtract(2, 'days').startOf('day') || current > columnMoment.add(2, 'days').endOf('day'));
                                default:
                                    return false;
                            }
                        }
                    }

                    const clientDeliveryZone = client ? client.defaultDeliveryZone?.id : null;
                    const deliveryDateDefault = getDefaultDeliveryDay(date, clientDeliveryZone, clientIdentity?.defaultMeals).format(
                        'YYYY-MM-DD'
                    );

                    return (
                        <td key={date}>
                            {!hasMealRecorded ? (
                                <div
                                    className="__no-meal"
                                    style={{textAlign: 'center', fontWeight: 700, padding: '12px 0'}}
                                >
                                    Pas de repas enregistré
                                </div>

                            ) : (
                                <>
                                    <div className="__delivery-day">
                                        <DatePicker
                                            value={
                                                menu
                                                    ? meal?.day
                                                        ? moment(meal.day)
                                                        : deliveryDateDefault
                                                            ? moment(deliveryDateDefault)
                                                            : getDefaultDeliveryDay(date, clientDeliveryZone)
                                                    : undefined
                                            }
                                            format="dddd DD MMMM YYYY"
                                            disabled
                                            disabledDate={(current) => isDisabledDate(current, date, clientDeliveryZone)}
                                        />
                                    </div>

                                    <Select
                                        className="__menu-select"
                                        allowClear
                                        value={menu ? menu.id : undefined}
                                        placeholder="Choisissez une formule"
                                        options={[
                                            { label: 'Aucun', value: null as unknown as string },
                                            ...dateMenus.map((m: any) => ({ label: m.mealDefinition.name, value: m.id })),
                                        ]}
                                        onChange={() => {}}
                                        disabled
                                    />
                                </>
                            )}
                        </td>
                    );
                })}
            </tr>

            {usedDishTypes.map((dishType: any) => {
                return (
                    <tr key={dishType.code}>
                        {weekDaysStr.map((date: any) => {
                            const meal = mealByDates[date];
                            const menu = menuByDates[date];

                            const dishes = menu
                                ? [
                                    ...menu.dishes
                                        .filter((d) => d.dish.type === dishType.code)
                                        .sort(ArrayService.cmpBy((d: any) => `${d.choiceNumber} ${d.dish.name}`))
                                        .map((d) => d.dish),

                                    ...(localWeekData
                                        ? localWeekData.mealMenus.flatMap((m: any) =>
                                            m.dishes
                                                .filter((localDish: any) => localDish.dish.type === 'extra' && dishType.code === 'extra')
                                                .map((localDish: any) => localDish.dish)
                                        )
                                        : []),
                                ].filter(ArrayService.filterUniqueEntity, (dish: any) => dish.id)
                                : [];

                            const selectedDishIds = new Set((meal?.dishes ?? []).map((x: any) => x.dish));

                            const selectedDishes = dishes.filter((d: any) => selectedDishIds.has(d.id));


                            const hasAlerts =
                                alerts.findIndex((a: any) => meal && a.meal.date === (meal as any).date && a.meal.mealPeriod === (meal as any).mealPeriod) > -1;

                            const hiddenSideDish =
                                dishType.code === 'side-dish' &&
                                menu &&
                                meal &&
                                menu.dishes.findIndex(
                                    (d) =>
                                        d.dish.type === 'dish' &&
                                        d.dish.noSideDish &&
                                        (meal as any).dishes.findIndex((dish: any) => dish.dish === d.dish.id) > -1
                                ) > -1;

                            const hiddenDishType = menu && !menu.dishTypes.includes(dishType.code);

                            const hasMealRecorded =
                                !!meal &&
                                !!menu &&
                                Array.isArray((meal as any).dishes) &&
                                (meal as any).dishes.length > 0;

                            if (!hasMealRecorded) {
                                return (
                                    <td key={date}></td>
                                );
                            }

                            return (
                                <td key={date} className={`${!hasAlerts && (meal as any)?.default != true ? '--success' : ''}`}>
                                    {!hiddenDishType && (
                                        <h3 className="__dish-type">
                                            <span>{dishType.label}</span>
                                        </h3>
                                    )}

                                    {hiddenSideDish && <div className="__hidden-msg">Aucun</div>}

                                    {!hiddenDishType && !hiddenSideDish && clientIdentity && menu && meal && (
                                        <>
                                            <div className="__choice" style={{ minHeight: dishType.code === 'side-dish' ? 95 : undefined }}>
                                                {selectedDishes.map((d: any) =>
                                                    ((menu.mealDefinition.name != 'Complément du Soir' &&
                                                            (clientIdentity as any).allowMultipleDishes &&
                                                            INCREMENTABLE_DISH_TYPES.includes(dishType.code)) ||
                                                        dishType.code === 'extra') ? (
                                                        <SelectMultipleDishButton key={d.id} dish={d} meal={meal as any} menu={menu as any} onChange={handleChange} />
                                                    ) : (
                                                        <SelectDishButton key={d.id} dish={d} meal={meal as any} dishes={dishes as any} menu={menu as any} onChange={handleChange} />
                                                    )
                                                )}
                                            </div>

                                            <AllergiesWarning meal={meal as any} dishes={dishes as any} clientIdentity={clientIdentity as any} />
                                        </>
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                );
            })}
        </>
    );
};

interface AllergiesWarningProps {
    dishes: Dish[];
    meal: EditedMeal;
    clientIdentity: ClientIdentity;
}

const AllergiesWarning: React.FunctionComponent<AllergiesWarningProps> = ({ dishes, meal, clientIdentity }) => {
    const selectedDish = dishes.find((d) => (meal as any).dishes.findIndex((dish: any) => dish.dish === d.id) > -1);
    if (!selectedDish) return null;

    const allergies =
        selectedDish && clientIdentity
            ? selectedDish.allergies.filter((a) => (clientIdentity as any).allergies.findIndex((ca: any) => ca.id === a.id) > -1)
            : [];

    if (allergies.length === 0) return null;

    return <AlertTag message={`Ce plat contient : ${allergies.map((a) => a.content).join(', ')}`} />;
};

interface SelectMultipleDishButtonProps {
    dish: Dish;
    meal: EditedMeal;
    menu: MealMenu;
    onChange: (newMeal: EditedMeal) => void;
}

const SelectMultipleDishButton: React.FunctionComponent<SelectMultipleDishButtonProps> = ({ dish, meal, menu, onChange }) => {
    const selectedQuantity = (meal as any).dishes.find((d: any) => d.dish === dish.id)?.quantity || 0;
    const isSelected = selectedQuantity > 0;

    function add() {
        const iDish = (meal as any).dishes.findIndex((d: any) => d.dish === dish.id);

        let newDishes: { dish: string; quantity: number }[] = [...((meal as any).dishes || [])];
        if (iDish === -1) newDishes.push({ dish: dish.id, quantity: 1 });
        else newDishes[iDish].quantity += 1;

        if (dish.type === 'dish' && (dish as any).noSideDish) {
            newDishes = newDishes.filter(
                (d) =>
                    (menu as any).dishes.findIndex(
                        (md: any) => md.dish.type === 'side-dish' && md.dish.id === d.dish
                    ) === -1
            );
        }

        onChange({ ...(meal as any), default: false, dishes: newDishes } as any);
    }

    function subtract() {
        const iDish = (meal as any).dishes.findIndex((d: any) => d.dish === dish.id);
        if (iDish === -1) return;

        const newDishes = [...((meal as any).dishes || [])];
        if (newDishes[iDish].quantity <= 1) newDishes.splice(iDish, 1);
        else newDishes[iDish].quantity -= 1;

        onChange({ ...(meal as any), default: false, dishes: newDishes } as any);
    }

    return (
        <div className="__select-multiple-dish-button">
            <Button
                type={isSelected ? 'primary' : undefined}
                className={isSelected ? 'selected-disabled' : undefined}
                disabled
                onClick={add}>
                {dish.name}
            </Button>
            <div className="__actions">
                <Button disabled onClick={subtract}>-</Button>
                <div className="__counter">{selectedQuantity}</div>
                <Button disabled onClick={add}>+</Button>
            </div>
        </div>
    );
};

interface SelectDishButtonProps {
    dish: Dish;
    dishes: Dish[];
    meal: EditedMeal;
    menu: MealMenu;
    onChange: (newMeal: EditedMeal) => void;
}

const SelectDishButton: React.FunctionComponent<SelectDishButtonProps> = ({ dish, meal, dishes, menu, onChange }) => {
    const isSelected = (meal as any).dishes.findIndex((d: any) => d.dish === dish.id) > -1;

    return (
        <Button
            type={isSelected ? 'primary' : undefined}
            className={isSelected ? 'selected-disabled' : undefined}
            disabled
        >
            {dish.name}
        </Button>
    );
};

interface SelectWithButtonsProps {
    onChange?: (value: string) => void;
    value?: string;
    options?: { label: string | ReactElement; value: string; filterValue: string }[];
    disabled?: boolean;
}

const SelectWithButtons: React.FunctionComponent<SelectWithButtonsProps> = ({ onChange, value, options, disabled }) => {
    function goToPrev() {
        if (!options || !onChange) return;
        const iOption = options.findIndex((o) => o.value === value);
        if (iOption > 0) onChange(options[iOption - 1].value);
    }

    function goToNext() {
        if (!options || !onChange) return;
        const iOption = options.findIndex((o) => o.value === value);
        if (iOption < options.length - 1) onChange(options[iOption + 1].value);
    }

    return (
        <div className="select-with-buttons">
            <Button icon={<UpOutlined />} shape="circle" onClick={goToPrev} disabled={disabled} />
            <Select
                dropdownClassName="select-dropdown"
                onChange={onChange}
                value={value}
                options={options}
                disabled={disabled}
                showSearch
                optionFilterProp="label"
                filterOption={(input, option: any) =>
                    option?.filterValue?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
            />
            <Button icon={<DownOutlined />} shape="circle" onClick={goToNext} disabled={disabled} />
        </div>
    );
};
